import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
	faArrowRight,
	faChevronDown,
	faDatabase,
	faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { JobsScrapeStatus } from "@/enum/scrape-enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";
import { useAppSelector } from "@/redux/hooks";
import { useGetListQuery } from "@/pages/Private/redux/list/list.api";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

import { getListSources, ListSourceItem } from "../utils/getListSources";
import { MapSourceStatusTone, mapSourceStatusLabel } from "../utils/mapSourceStatusLabel";
import { rerunListGate, rerunListHintKey } from "../utils/rerunListGate";
import { getListConfigurationStatus } from "../utils/useListConfigurationStatus";

interface ListSourcesDropdownProps {
	listId: number;
	/** Styling of the sibling header links, so the trigger matches them exactly. */
	triggerClassName: string;
	iconClassName: string;
}

/** The chip tone tokens the menu already uses elsewhere (running / success / error / neutral). */
const STATUS_TONE_CLASS: Record<MapSourceStatusTone, string> = {
	neutral: "bg-inactive-item text-text-meta",
	running: "bg-status-running-light text-status-running",
	success: "bg-status-success-light text-status-success",
	error: "bg-status-error-light text-status-error",
};

/**
 * Stateful Sources control for the list header (PREC-3703). With no source yet it is an
 * "Add a source" link; once sources exist it becomes a dropdown listing them, with "Add a source" /
 * "View all sources" links (both land on the list's source page, which is viewer and editor).
 * Source arrays only travel with the *full* list (GET list/:id — already loaded on the detail page),
 * so it reads this list's own query result and falls back to the slice's `currentFullList`; until
 * either resolves for this list it falls back to a plain "Sources" link.
 *
 * A Google Maps source shows its stored search, its city/country and its run state, plus the
 * existing Re-Run path when it could not be run (PREC-4994). Non-Maps rows are unchanged.
 */
export const ListSourcesDropdown: FunctionComponent<ListSourcesDropdownProps> = ({
	listId,
	triggerClassName,
	iconClassName,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const { data: queriedList } = useGetListQuery(listId, { skip: !listId });
	const { currentList, currentFullList } = useAppSelector(listSelector);

	// Read the query's own result first: returning to a list inside RTK Query's cache lifetime
	// serves `getList` without dispatching a new `matchFulfilled`, so the slice can still hold the
	// list we just navigated away from (PREC-4994). The slice stays as the fallback for the first
	// paint, before the query has data — it is written by the same `matchFulfilled`, so it is never
	// fresher for this id. Either way only trust a full list for *this* list (see
	// useListConfigurationStatus, PREC-3621).
	const fullList =
		queriedList?.id === listId
			? queriedList
			: currentFullList && currentFullList.id === listId
				? currentFullList
				: null;
	const sources: ListSourceItem[] | undefined = fullList ? getListSources(fullList) : undefined;
	// The lean list is the polled one `RerunListButton` reads, so the hint's gate must use it too or
	// it lags a run that just started/finished until the next full-list load (PREC-4994). Until a
	// lean list for this id arrives, the full list is the best answer available.
	const liveList = currentList && currentList.id === listId ? currentList : fullList;
	// The full list carries no lead counts (only source arrays), so pass `null` rather than let the
	// helper read them off it; for a Maps row the source is present and the state is PROVIDED.
	const rerunHint = fullList
		? rerunListHintKey(
				rerunListGate(liveList ?? fullList, getListConfigurationStatus(fullList, null))
			)
		: "";

	const sourcePage = `${ROUTE_CONFIG.LISTS}/create/source/${listId}`;

	// Full list still loading, or no sources yet: a plain link to the source page. The label
	// switches to the "Add a source" CTA once we know the list is empty.
	if (!sources || sources.length === 0) {
		return (
			<Link className={triggerClassName} to={sourcePage}>
				<Icon className={iconClassName} icon={faDatabase} />
				<span>{sources ? t("list.sources.dropdown.add") : t("list.sources.title")}</span>
			</Link>
		);
	}

	const goToSourcePage = () => {
		setAnchorEl(null);
		navigate(sourcePage);
	};

	// A source row deep-links to the source page with that source opened for editing
	// (consumed by useEditSourceParam on ListEditSource).
	const goToSourceEditor = (sourceId: number) => {
		setAnchorEl(null);
		navigate(`${sourcePage}?editSource=${sourceId}`);
	};

	const renderSourceRow = (source: ListSourceItem) => {
		// Some source types (e.g. LI_POST) can have no resolvable name — never render blank.
		const label = source.name || t("list.sources.dropdown.unnamed");

		if (!source.maps) {
			return (
				<button
					className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-background-light"
					key={source.key}
					type="button"
					onClick={() => goToSourceEditor(source.id)}
				>
					<Icon className="h-3.5 w-3.5 shrink-0 text-text-meta" icon={faDatabase} />
					<span className="truncate text-[13px] font-semibold text-text" title={label}>
						{label}
					</span>
				</button>
			);
		}

		const { maps } = source;
		const status = mapSourceStatusLabel(maps.status);
		const location = [maps.city, maps.country]
			.map((part) => part?.trim())
			.filter(Boolean)
			.join(", ");
		const meta = location
			? `${t("list.sources.maps.type")} · ${location}`
			: t("list.sources.maps.type");

		return (
			<div className="rounded-lg hover:bg-background-light" key={source.key}>
				<button
					className="flex w-full cursor-pointer items-start gap-2.5 rounded-lg px-3 py-2 text-left"
					type="button"
					onClick={() => goToSourceEditor(source.id)}
				>
					<Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-meta" icon={faDatabase} />
					<span className="min-w-0 flex-1">
						<span className="block truncate text-[13px] font-semibold text-text" title={label}>
							{label}
						</span>
						<span className="block truncate text-xs text-text-meta" title={maps.query || undefined}>
							{maps.query}
						</span>
						<span className="block truncate text-xs text-text-tertiary">{meta}</span>
						<span className="mt-1 flex items-center gap-1.5">
							<span
								className={classNames(
									"inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold",
									STATUS_TONE_CLASS[status.tone]
								)}
							>
								{t(status.key)}
							</span>
							{maps.status === JobsScrapeStatus.DONE && maps.leadCount !== undefined && (
								<span className="text-[11px] font-medium text-text-meta">
									{t("list.sources.maps.leadCount", { count: maps.leadCount })}
								</span>
							)}
						</span>
					</span>
				</button>
				{maps.status === JobsScrapeStatus.ERROR && (
					<p
						className="pb-2 pl-[34px] pr-3 text-[11px] leading-snug text-status-error"
						data-testid="list-sources-map-hint"
					>
						{t(rerunHint)}
					</p>
				)}
			</div>
		);
	};

	return (
		<>
			<button
				className={classNames(triggerClassName, "cursor-pointer")}
				data-testid="list-sources-dropdown-trigger"
				type="button"
				onClick={(event) => {
					// Capture before the updater runs — `currentTarget` is nulled after dispatch.
					const trigger = event.currentTarget as HTMLElement;

					setAnchorEl((current) => (current ? null : trigger));
				}}
			>
				<Icon className={iconClassName} icon={faDatabase} />
				<span>{t("list.sources.title")}</span>
				<Icon className="h-2.5 w-2.5 opacity-60" icon={faChevronDown} />
			</button>

			{anchorEl && (
				<AnchoredPopover anchorEl={anchorEl} width={300} onClose={() => setAnchorEl(null)}>
					<div
						className="overflow-hidden rounded-xl border border-border-light bg-white p-1.5 shadow-lg"
						data-testid="list-sources-dropdown-menu"
					>
						{sources.map(renderSourceRow)}
						<div className="mt-1 flex flex-col items-start gap-0.5 border-t border-border-light px-1.5 pb-1 pt-1.5">
							<button
								className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[13px] font-semibold text-brand-light hover:bg-background-light"
								type="button"
								onClick={goToSourcePage}
							>
								<Icon className="h-3 w-3" icon={faPlus} />
								{t("list.sources.dropdown.add")}
							</button>
							<button
								className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-[13px] font-semibold text-brand-light hover:bg-background-light"
								type="button"
								onClick={goToSourcePage}
							>
								{t("list.sources.dropdown.viewAll")}
								<Icon className="h-3 w-3" icon={faArrowRight} />
							</button>
						</div>
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
